package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import dev.lingvo.shared.FileFormat
import dev.lingvo.shared.FileDto
import dev.lingvo.shared.ImportPlanDto
import dev.lingvo.shared.ImportRequest
import dev.lingvo.shared.ImportResultDto
import dev.lingvo.shared.WorkspaceDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Async
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectAuthoritySubject
import dev.lingvo.site.state.VerifiedProjectRoute
import dev.lingvo.site.state.requestProjectAction
import kotlinx.coroutines.CancellationException
import org.w3c.files.File

/** A click captures input and authority before launching; retirement never grants them again. */
internal data class ImportPreviewOperation(
    val generation: Int,
    val request: ImportRequest,
    val claim: ProjectActionClaim,
)

/** Single-file import state, scoped to the verified project, authenticated generation and role. */
internal class ImportPreviewState(private val subject: ProjectAuthoritySubject?) {
    var file by mutableStateOf<File?>(null)
        private set
    var content by mutableStateOf<String?>(null)
        private set
    var locale by mutableStateOf("")
        private set
    var format by mutableStateOf<FileFormat?>(null)
        private set
    var path by mutableStateOf("")
        private set
    var destination by mutableStateOf<FileDto?>(null)
        private set
    var destinationFiles by mutableStateOf<List<FileDto>>(emptyList())
        private set
    var destinationFilesLoading by mutableStateOf(true)
        private set
    var destinationFilesFailure by mutableStateOf<Async.Failure?>(null)
        private set
    var plan by mutableStateOf<ImportPlanDto?>(null)
        private set
    var planned by mutableStateOf<ImportRequest?>(null)
        private set
    var confirmation by mutableStateOf("")
    var busy by mutableStateOf(false)
        private set
    var message by mutableStateOf<String?>(null)
        private set
    var done by mutableStateOf<String?>(null)
        private set
    var inspectRequired by mutableStateOf(false)
        private set
    var exportRefreshToken by mutableStateOf(0)
        private set
    private var generation = 0
    private var explicitPath = false

    val destinationAvailable: Boolean get() = destination?.let { selected ->
        !destinationFilesLoading && destinationFilesFailure == null &&
            destinationFiles.any { it.id == selected.id && it.path == selected.path && it.format == selected.format }
    } ?: true

    val readyToPlan: Boolean get() = !destinationFilesLoading && file != null && content != null && format != null && locale.isNotBlank() &&
        path.isNotBlank() && destinationAvailable && destinationFilesFailure?.let { it.isRefusal || it.isMissing } != true

    fun chooseFile(selected: File, detectedLocale: String, detectedFormat: FileFormat?) {
        val defaultPath = !explicitPath
        retire()
        file = selected
        content = null
        locale = detectedLocale
        if (destination == null) {
            if (defaultPath) path = selected.name
            format = detectedFormat
        }
    }

    fun changeDestination(selected: FileDto?) {
        if (selected?.id == destination?.id && selected?.path == destination?.path && selected?.format == destination?.format) return
        retire()
        destination = selected
        explicitPath = true
        if (selected != null) {
            path = selected.path
            format = selected.format
        }
    }

    fun changePath(value: String) {
        if (destination != null) return
        explicitPath = true
        if (path == value) return
        retire()
        path = value
    }

    /** Read retirement is synchronous; an adjacent old Apply cannot use unverified metadata. */
    fun destinationFilesStarted() {
        if (destinationFilesLoading) return
        destinationFilesLoading = true
        destinationFilesFailure = null
        if (destination != null) invalidateDestinationPreview()
    }

    fun destinationFilesLoaded(files: List<FileDto>) {
        destinationFiles = files
        destinationFilesLoading = false
        destinationFilesFailure = null
        val selected = destination ?: return
        val current = files.singleOrNull { it.id == selected.id }
        if (current == null) {
            invalidateDestinationPreview()
        } else if (current.path != selected.path || current.format != selected.format) {
            invalidateDestinationPreview()
            destination = current
            path = current.path
            if (format == selected.format) format = current.format
        }
    }

    fun destinationFilesFailed(failure: Throwable) {
        val api = failure as? ApiException
        destinationFilesLoading = false
        destinationFilesFailure = Async.Failure(
            failure.message?.takeIf(String::isNotBlank)
                ?: tr("app.projects.slug.import_preview_state.files_read_failed", "Couldn't read the project files. Retry the file list."),
            api?.status,
            api?.code,
        )
        if (destination != null || destinationFilesFailure?.let { it.isRefusal || it.isMissing } == true) invalidateDestinationPreview()
    }

    private fun invalidateDestinationPreview() {
        generation++
        clearPreview()
        busy = false
    }

    fun fileLoaded(selected: File, text: String) {
        if (selected != file || content == text) return
        retire()
        content = text
    }

    fun fileFailed(selected: File) {
        if (selected != file) return
        retire()
        message = trf("app.projects.slug.data.couldnt_read_file", "Couldn't read {file}. Choose it again, or pick another file.", "file" to selected.name)
    }

    fun changeLocale(value: String) {
        if (value == locale) return
        retire()
        locale = value
    }

    fun changeFormat(value: FileFormat?) {
        if (value == format) return
        retire()
        format = value
    }

    fun beginPreview(claim: ProjectActionClaim): ImportPreviewOperation? {
        if (busy || !readyToPlan || claim.subject != subject) return null
        val request = ImportRequest(path, format!!, locale.trim(), content!!, fileId = destination?.id)
        retire()
        busy = true
        return ImportPreviewOperation(generation, request, claim)
    }

    fun beginCommit(claim: ProjectActionClaim): ImportPreviewOperation? {
        if (busy || !readyToPlan || claim.subject != subject) return null
        val current = plan ?: return null
        val request = planned ?: return null
        val count = confirmation.trim().toIntOrNull()
        if (current.requiresForce && count != current.willObsolete) return null
        generation++
        busy = true
        message = null
        done = null
        inspectRequired = false
        return ImportPreviewOperation(generation,
            request.copy(planId = current.id, options = request.options.copy(confirmObsoleteCount = count)), claim)
    }

    suspend fun prepare(
        operation: ImportPreviewOperation,
        currentRoute: () -> VerifiedProjectRoute?,
        currentMemberships: () -> List<WorkspaceDto>,
    ) = run(operation, currentRoute, currentMemberships, commit = false) {
        val result = ApiClient.planImport(operation.claim.routeSlug, operation.request)
        if (owns(operation, currentRoute, currentMemberships)) {
            if (result.path != operation.request.path || result.locale != operation.request.locale) {
                message = tr("app.projects.slug.import_preview_state.the_preview_did_not_match_the", "The preview did not match the selected destination. Prepare a fresh preview.")
            } else {
                plan = result
                planned = operation.request
                confirmation = ""
            }
        }
    }

    suspend fun apply(
        operation: ImportPreviewOperation,
        currentRoute: () -> VerifiedProjectRoute?,
        currentMemberships: () -> List<WorkspaceDto>,
    ) = run(operation, currentRoute, currentMemberships, commit = true) {
        val result = ApiClient.commitImport(operation.claim.routeSlug, operation.request)
        if (owns(operation, currentRoute, currentMemberships)) acknowledge(result)
    }

    private suspend fun run(
        operation: ImportPreviewOperation,
        currentRoute: () -> VerifiedProjectRoute?,
        currentMemberships: () -> List<WorkspaceDto>,
        commit: Boolean,
        transport: suspend () -> Unit,
    ) {
        if (!owns(operation, currentRoute, currentMemberships)) return
        try {
            requestProjectAction(operation.claim, currentRoute, currentMemberships, transport)
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (failure: Throwable) {
            if (owns(operation, currentRoute, currentMemberships)) {
                if (commit) commitFailed(failure) else message = failure.message
            }
        } finally {
            // Both request generation and live grant must still own this state. A's late finally
            // cannot release B's spinner, and a lost role cannot publish a previously granted read.
            if (owns(operation, currentRoute, currentMemberships)) busy = false
        }
    }

    private fun owns(operation: ImportPreviewOperation, currentRoute: () -> VerifiedProjectRoute?, currentMemberships: () -> List<WorkspaceDto>): Boolean =
        generation == operation.generation && operation.claim.subject == subject &&
            operation.claim.remainsAuthorized(currentRoute(), currentMemberships())

    private fun acknowledge(result: ImportResultDto) {
        exportRefreshToken++
        clearPreview()
        done = trf("app.projects.slug.data.import_result_counts", "{added} added, {updated} updated, {obsoleted} obsoleted, {skipped} skipped.",
            "added" to result.added, "updated" to result.updated, "obsoleted" to result.obsolete, "skipped" to result.skipped)
    }

    private fun commitFailed(failure: Throwable) {
        val api = failure as? ApiException
        when (api?.code) {
            "plan_stale" -> {
                clearPreview()
                message = tr("app.projects.slug.import_preview_state.the_project_changed_while_you_were", "The project changed while you were reading. Prepare a new preview before importing.")
            }
            "plan_expired" -> {
                clearPreview()
                message = tr("app.projects.slug.import_preview_state.this_preview_expired_prepare_a_new", "This preview expired. Prepare a new preview to see what the import would do now.")
            }
            "plan_content_mismatch" -> {
                clearPreview()
                message = tr("app.projects.slug.import_preview_state.the_file_no_longer_matches_this", "The file no longer matches this preview. Prepare a new preview before importing.")
            }
            "plan_not_found" -> {
                clearPreview()
                message = tr("app.projects.slug.import_preview_state.preview_unavailable", "This preview is no longer available. Prepare a new preview before importing.")
            }
            "plan_consumed" -> {
                clearPreview()
                inspectRequired = true
                message = tr("app.projects.slug.import_preview_state.this_import_may_already_have_applied", "This import may already have applied. Inspect the current project before preparing another import.")
            }
            else -> {
                if (api == null || api.status == 0 || api.status >= 500) {
                    clearPreview()
                    inspectRequired = true
                    message = tr("app.projects.slug.import_preview_state.we_couldnt_confirm_whether_the_import", "We couldn't confirm whether the import applied. Inspect the current project before preparing another import. This write will not be retried automatically.")
                } else message = api.message
            }
        }
    }

    private fun clearPreview() {
        plan = null
        planned = null
        confirmation = ""
    }

    private fun retire() {
        generation++
        clearPreview()
        busy = false
        message = null
        done = null
        inspectRequired = false
    }
}
